import type { ReactNode } from "react";
import React, { useEffect, useMemo, useState } from "react";
import type { ColumnHeader, RowData } from "@/components/MyTable";
import { Pagination } from "@/components/Pagination";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useDebouncedInput } from "@/hooks/useDebouncedInput";
import clsx from "clsx";
import { Search, TriangleAlert, X } from "lucide-react";
import { PatientV2CircularLoader } from "../shared/PatientV2CircularLoader";
import {
  patientV2CardFieldLabelClass,
  patientV2CardFieldValueClass,
  patientV2InvalidBadgeClass,
} from "../constants";
import { PatientV2ExpandableCardActions } from "./PatientV2ExpandableCardActions";
import { HoverTooltip } from "@/components/ui/tooltip";

const CRITICAL_ALERT_COL_ID = "__criticalAlert";

type MoreActions = {
  label?: string;
  icon?: React.ReactNode;
  onClick?: (data: any) => void;
  isShow: (data?: any) => boolean;
  isDisabled?: (data?: any) => boolean;
  render?: (data: any) => React.ReactNode;
};

function isCriticalRow(row: RowData) {
  return String(row?.status ?? "").toLowerCase() === "critical";
}

function isInvalidRow(row: RowData) {
  return !!(row as { invalid?: boolean })?.invalid;
}

function displayValue(value: unknown): string {
  if (value == null) return "—";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    const str = String(value).trim();
    return str.length ? str : "—";
  }
  if (Array.isArray(value)) {
    if (!value.length) return "—";
    return value
      .map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v)))
      .join(", ");
  }
  return String(value);
}

function buildRowSearchHaystack(
  row: RowData,
  viewRow: RowData,
  columnHeaders: ColumnHeader[],
): string {
  if (columnHeaders.length > 0) {
    return columnHeaders
      .map((header) => displayValue(viewRow?.[header.id] ?? row?.[header.id]))
      .join(" ")
      .toLowerCase();
  }
  const values: string[] = [];
  const collect = (value: unknown) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(collect);
      return;
    }
    if (typeof value === "object") {
      Object.values(value as Record<string, unknown>).forEach(collect);
      return;
    }
    const text = String(value).trim();
    if (text) values.push(text);
  };
  collect(viewRow);
  collect(row);
  return values.join(" ").toLowerCase();
}


export type RenderCardRowContext = {
  row: RowData;
  viewRow: RowData;
  index: number;
  visibleHeaders: ColumnHeader[];
  isCritical: boolean;
  actions: MoreActions[];
};

export type ClientCardRowPaginatedTableProps = {
  columnHeaders: ColumnHeader[];
  columnsData: RowData[];
  viewColumnData?: RowData[];
  loading?: boolean;
  defaultLimit?: number;
  clickRow?: (row: RowData) => void;
  className?: string;
  isIncludeSearch?: boolean;
  searchPlaceholder?: string;
  renderRowCard?: (ctx: RenderCardRowContext) => ReactNode;
  pageSizeOptions?: number[];
  moreActions?: MoreActions[];
};

const ClientCardRowPaginatedTable: React.FC<ClientCardRowPaginatedTableProps> = ({
  columnHeaders,
  columnsData,
  viewColumnData,
  loading = false,
  defaultLimit = 10,
  clickRow = () => {},
  className,
  isIncludeSearch = true,
  searchPlaceholder = "Search...",
  renderRowCard,
  pageSizeOptions = [10, 30, 50, 100],
  moreActions = [],
}) => {
  const { input, setInput, debouncedInput } = useDebouncedInput("", 300);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);

  const searchQuery = debouncedInput.trim().toLowerCase();

  const filteredPairs = useMemo(() => {
    const cols = columnsData || [];
    const viewsRaw = viewColumnData;
    const views =
      Array.isArray(viewsRaw) && viewsRaw.length === cols.length ? viewsRaw : cols;
    if (!isIncludeSearch || !searchQuery) {
      return { cols, views };
    }
    const fc: RowData[] = [];
    const fv: RowData[] = [];
    cols.forEach((row, i) => {
      const view = views[i] ?? row;
      const haystack = buildRowSearchHaystack(row, view, columnHeaders);
      if (haystack.includes(searchQuery)) {
        fc.push(row);
        fv.push(view);
      }
    });
    return { cols: fc, views: fv };
  }, [columnsData, viewColumnData, searchQuery, isIncludeSearch, columnHeaders]);

  const totalRows = filteredPairs.cols.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / limit));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  useEffect(() => {
    setPage(1);
  }, [debouncedInput, limit]);

  const displayPairs = useMemo(() => {
    const start = (page - 1) * limit;
    const cols = filteredPairs.cols.slice(start, start + limit);
    const views = filteredPairs.views.slice(start, start + limit);
    return cols.map((row, index) => ({
      row,
      viewRow: views[index] ?? row,
      index,
    }));
  }, [filteredPairs, page, limit]);

  const visibleHeaders = columnHeaders.filter((h) => h.id !== CRITICAL_ALERT_COL_ID);

  return (
    <Card
      className={clsx(
        "teachers gap-2 rounded-md border border-grey bg-transparent p-0 shadow-none",
        className,
      )}
    >
      {isIncludeSearch ? (
        <CardHeader className="px-3 pb-0 pt-3">
          <div className="relative w-full md:w-64 md:basis-1/2">
            <Search className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder={searchPlaceholder}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="ignore h-10 bg-background pl-9 pr-9"
            />
            {input ? (
              <button
                type="button"
                onClick={() => setInput("")}
                className="text-muted-foreground hover:text-foreground absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </CardHeader>
      ) : null}

      <CardContent className="space-y-4 p-0 pt-2 pb-2">
        {loading ? (
          <PatientV2CircularLoader />
        ) : (
          <div className="space-y-3 px-3">
            {displayPairs.length === 0 ? (
              <div className="text-muted-foreground rounded-lg border border-dashed border-border/50 bg-transparent p-4 text-sm">
                No records found.
              </div>
            ) : (
              displayPairs.map(({ row, viewRow, index }) => {
                const rowId = String(row?._id ?? row?.id ?? `row-${index}`);
                const isCritical = isCriticalRow(row);
                const isInvalid = isInvalidRow(row);
                const rowActions = (moreActions || []).filter((a) => a?.isShow?.(row));
                const ctx: RenderCardRowContext = {
                  row,
                  viewRow,
                  index,
                  visibleHeaders,
                  isCritical,
                  actions: rowActions,
                };

                if (renderRowCard) {
                  return (
                    <div
                      key={rowId}
                      onClick={() => clickRow(row)}
                      className="cursor-pointer"
                    >
                      {renderRowCard(ctx)}
                    </div>
                  );
                }

                return (
                  <div
                    key={rowId}
                    onClick={() => clickRow(row)}
                    className="rounded-lg border border-border bg-card p-3 shadow-sm transition-colors hover:bg-card"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 md:grid-cols-2">
                        {visibleHeaders.map((h) => (
                          <div key={h.id} className="min-w-0">
                            <p className={patientV2CardFieldLabelClass}>{h.label}</p>
                            <p className={patientV2CardFieldValueClass}>
                              {displayValue(viewRow?.[h.id])}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="flex w-[9.5rem] shrink-0 items-center justify-end gap-2 self-center">
                        <div className="flex h-6 w-[4.5rem] items-center justify-end">
                          {isInvalid ? (
                            <Badge className={`${patientV2InvalidBadgeClass} uppercase`}>
                              Invalid
                            </Badge>
                          ) : null}
                        </div>
                        {isCritical && (
                          <HoverTooltip
                            content="This reading is critical and may need attention"
                            side="top"
                          >
                            <span
                              className="inline-flex shrink-0"
                              role="img"
                              aria-label="Critical reading"
                            >
                              <TriangleAlert
                                className="h-5 w-5 text-destructive"
                                aria-hidden
                              />
                            </span>
                          </HoverTooltip>
                        ) }
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center">
                          {rowActions.length > 0 ? (
                            <PatientV2ExpandableCardActions
                              actions={rowActions}
                              row={row}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {!loading ? (
          <div className="px-3">
            <Pagination
              page={page}
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              total={pageCount}
              pageSizeOptions={pageSizeOptions}
            />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ClientCardRowPaginatedTable;
